import React from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Divider,
  StepContent,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import { useTranslation } from "../../../hooks/useTranslation";
import { useCustom } from "@refinedev/core";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import GavelIcon from "@mui/icons-material/Gavel";
import PaymentIcon from "@mui/icons-material/Payment";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";

const createDescription = (
  translate: (_key: string) => string,
  step: string,
  desc1: string,
  desc2: string,
  desc3?: string,
  boldText1?: string,
  boldText2?: string,
) => (
  <>
    {translate(`${step}.${desc1}`)}{" "}
    {boldText1 && <b>{translate(`${step}.${boldText1}`)}</b>}{" "}
    {translate(`${step}.${desc2}`)}{" "}
    {boldText2 && <b>{translate(`${step}.${boldText2}`)}</b>}{" "}
    {desc3 && translate(`${step}.${desc3}`)}
  </>
);

const steps = (translate: (_key: string) => string) => [
  {
    label: translate("step1.label"),
    description: createDescription(
      translate,
      "step1",
      "desc1",
      "desc2",
      "desc3",
      "settings",
      "viewAll",
    ),
  },
  {
    label: translate("step2.label"),
    description: createDescription(
      translate,
      "step2",
      "desc1",
      "desc2",
      "desc3",
      "forwarding",
      "addAddress",
    ),
  },
  {
    label: translate("step3.label"),
    description: createDescription(
      translate,
      "step3",
      "desc1",
      "desc2",
      undefined,
      "next",
    ),
  },
];

export const MailForwardInstructions = () => {
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.stores.mailNotification.mail",
  );
  const [copied, setCopied] = React.useState<boolean>(false);

  const [activeStep, setActiveStep] = React.useState<number>(0);

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);
  const handleReset = () => setActiveStep(0);

  const { data } = useCustom({
    url: `seller/mail`,
    method: "get",
  });
  const mail = data?.data;

  const handleCopy = () => {
    navigator.clipboard.writeText(String(mail));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const stepData = steps(translate);

  const notifications = [
    {
      key: "notification.item1",
      icon: <LocalShippingIcon />,
    },
    {
      key: "notification.item2",
      icon: <GavelIcon />,
    },
    {
      key: "notification.item3",
      icon: <PaymentIcon />,
    },
    {
      key: "notification.item4",
      icon: <MonetizationOnIcon />,
    },
    {
      key: "notification.item5",
      icon: <AssignmentReturnIcon />,
    },
  ];

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        {translate("info")}
      </Alert>

      <Typography variant="body1" gutterBottom>
        {translate("notification.desc")}
      </Typography>
      <List dense>
        {notifications.map((item) => (
          <ListItem key={item.key}>
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={translate(item.key)} />
          </ListItem>
        ))}
      </List>
      <Typography variant="body1" gutterBottom>
        {translate("notification.footer")}
      </Typography>

      <Divider sx={{ my: 3 }} />

      <Alert
        severity="info"
        sx={{ mb: 2, cursor: "pointer" }}
        onClick={handleCopy}
      >
        {copied ? (
          <Typography variant="body2" fontWeight={700}>
            {translateWithoutPrefix("common.copied")}
          </Typography>
        ) : (
          <>
            <Typography variant="body2" component="span" fontWeight={500}>
              {translate("email.label")}:
            </Typography>
            <Typography
              variant="body2"
              component="span"
              fontWeight={700}
              sx={{ ml: 0.5 }}
            >
              {String(mail)}
            </Typography>
          </>
        )}
      </Alert>
      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" fontWeight={600} gutterBottom>
        {translate("guide.title")}
      </Typography>

      <Stepper activeStep={activeStep} orientation="vertical">
        {stepData.map((step) => (
          <Step key={step.label}>
            <StepLabel>{step.label}</StepLabel>
            <StepContent>
              <Typography>{step.description}</Typography>
              <Box sx={{ mt: 2 }}>
                {activeStep !== stepData.length - 1 && (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    sx={{ mt: 1, mr: 1 }}
                    disabled={activeStep === stepData.length - 1}
                  >
                    {translate("buttons.next")}
                  </Button>
                )}
                {activeStep !== 0 && (
                  <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    sx={{ mt: 1 }}
                  >
                    {translate("buttons.back")}
                  </Button>
                )}
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>

      {activeStep === stepData.length && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="success">{translate("complete.message")}</Alert>
          <Button onClick={handleReset} sx={{ mt: 2 }} variant="outlined">
            {translate("buttons.reset")}
          </Button>
        </Box>
      )}
    </>
  );
};
